import moment from 'moment/moment';
import { action, computed, IValueDidChange, observable, observe } from 'mobx';
import { PublisherFacebookPagesQuery_publisherFacebookPages as PublisherFacebookPage } from '@queries/selfService/__generated__/PublisherFacebookPagesQuery';
import {
  CampaignSettingsQuery_campaign_campaignFacebookPages as CampaignFacebookPage,
  CampaignSettingsQuery_campaign_organicUploadSettings as OrganicUploadSettings,
  CampaignSettingsQuery_campaign_articleExpirationSettings as ArticleExpirationSettings
} from '@queries/selfService/__generated__/CampaignSettingsQuery';
import {
  UserOsEnum,
  UserDeviceEnum,
  DevicePlatformsEnum,
  SelfServiceCampaignUseCaseEnum,
  FacebookPositionsEnum,
  InstagramPositionsEnum,
  MessengerPositionsEnum,
  PublisherPlatformsEnum,
  OrganicUploadSettingsCreativeCallToActionEnum,
  OrganicUploadSettingsTypeEnum,
  ArticleExpirationSettingsRuleTypeEnum,
  SelfServiceCampaignInput,
  ArticleExpirationSettingsInput
} from '@queries/globalTypes';

class CampaignSettingsStore {
  /* ----------------------------------------------------------- */
  /* Root Attributes
  /* ----------------------------------------------------------- */
  @observable id: number | null = null;
  @action setId = (value: number) => (this.id = value);

  @observable isDialogOen = false;
  @action setIsDialogOpen = (value: boolean) => (this.isDialogOen = value);

  @observable isDrawerOpen = false;
  @action setIsDrawerOpen = (value: boolean) => (this.isDrawerOpen = value);

  @observable isLiveCampaign: boolean = false;
  @action setIsLiveCampaign = (value: boolean) => (this.isLiveCampaign = value);
  /* ----------------------------------------------------------- */
  /* General Attributes
  /* ----------------------------------------------------------- */
  @observable name = '';
  @action setName = (value: string) => (this.name = value);

  @observable end: string = moment().add(1, 'days').format('YYYY-MM-DD');
  @action setEnd = (value: string) => (this.end = value);

  @observable useCase: SelfServiceCampaignUseCaseEnum | null = null;
  @action setUseCase = (value: SelfServiceCampaignUseCaseEnum | null) => (this.useCase = value);
  /* ----------------------------------------------------------- */
  /* Budget And Optimization Attributes
  /* ----------------------------------------------------------- */
  @observable defaultArbitrageOptimizationStrategy: string | null = null;

  @observable defaultDailyBudget: number = 0;
  @action setDefaultDailyBudget = (value: number) => (this.defaultDailyBudget = value);

  @observable defaultObjective: string = 'LINK_CLICKS';
  @action setDefaultObjective = (value: string) => (this.defaultObjective = value);

  @observable optimizationGoal: string | null = 'LINK_CLICKS';
  @action setOptimizationGal = (value: string | null) => (this.optimizationGoal = value);
  /* ----------------------------------------------------------- */
  /* URL & Domains Settings Attributes
  /* ----------------------------------------------------------- */
  @observable whitelistDomains: string = '';
  @action setWhitelistDomains = (value: string) => (this.whitelistDomains = value);

  @observable utmParameters: string = '';
  @action setUtmParameters = (value: string) => (this.utmParameters = value);

  @observable adMacroTags: string = '';
  @action setAdMacroTags = (value: string) => (this.adMacroTags = value);

  @observable whitelistedUrlParams: string = '';
  @action setWhitelistedUrlParams = (value: string) => (this.whitelistedUrlParams = value);

  @observable urlSegment: string = '';
  @action setUrlSegment = (value: string) => (this.urlSegment = value);
  /* ----------------------------------------------------------- */
  /* Technical Attributes
  /* ----------------------------------------------------------- */
  @observable campaignFacebookPages: CampaignFacebookPage[] = [];
  @action setCampaignFacebookPages = (value: CampaignFacebookPage[]) => (this.campaignFacebookPages = value);

  @observable unifiedTrackingPixels: Record<string, string>[] = [];
  @action setUnifiedTrackingPixels = (value: Record<string, string>[]) => (this.unifiedTrackingPixels = value);
  /* ----------------------------------------------------------- */
  /* Placement Attributes
  /* ----------------------------------------------------------- */
  @observable automaticAdPlacementEnabled = false;
  @action setAutomaticAdPlacementEnabled = (value: boolean) => (this.automaticAdPlacementEnabled = value);

  @observable defaultPlacementPublisherPlatforms: PublisherPlatformsEnum[] | null = null;
  @action setDefaultPlacementPublisherPlatforms = (value: PublisherPlatformsEnum[]) =>
    (this.defaultPlacementPublisherPlatforms = value);

  @observable defaultPlacementDevicePlatforms: DevicePlatformsEnum[] | null = null;
  @action setDefaultPlacementDevicePlatforms = (value: DevicePlatformsEnum[]) =>
    (this.defaultPlacementDevicePlatforms = value);

  @observable defaultPlacementFacebookPositions: FacebookPositionsEnum[] | null = null;
  @action setDefaultPlacementFacebookPositions = (value: FacebookPositionsEnum[]) =>
    (this.defaultPlacementFacebookPositions = value);

  @observable defaultPlacementInstagramPositions: InstagramPositionsEnum[] | null = null;
  @action setDefaultPlacementInstagramPositions = (value: InstagramPositionsEnum[]) =>
    (this.defaultPlacementInstagramPositions = value);

  @observable defaultPlacementMessengerPositions: MessengerPositionsEnum[] | null = null;
  @action setDefaultPlacementMessengerPositions = (value: MessengerPositionsEnum[]) =>
    (this.defaultPlacementMessengerPositions = value);

  @observable defaultUserDevice: UserDeviceEnum[] = [];
  @action setDefaultUserDevice = (value: UserDeviceEnum[]) => (this.defaultUserDevice = value);

  @observable defaultUserOs: UserOsEnum[] | null = null;
  @action setDefaultUserOs = (value: UserOsEnum[] | null) => (this.defaultUserOs = value);
  /* ----------------------------------------------------------- */
  /* Targeting Attributes
  /* ----------------------------------------------------------- */
  @observable defaultAgeMin: number = 18;
  @action setDefaultAgeMin = (value: number) => (this.defaultAgeMin = value);

  @observable defaultAgeMax: number = 65;
  @action setDefaultAgeMax = (value: number) => (this.defaultAgeMax = value);

  @observable defaultGender = 0;
  @action setDefaultGender = (value: number) => (this.defaultGender = value);

  @observable language = 'en';
  @action setLanguage = (value: string) => (this.language = value);

  @observable defaultLocalesFromLanguage = false;
  @action setDefaultLocalesFromLanguage = (value: boolean) => (this.defaultLocalesFromLanguage = value);

  @observable defaultCountries: string[] = [];
  @action setDefaultCountries = (value: string[]) => (this.defaultCountries = value);

  @observable defaultRegions: Record<string, string>[] = [];
  @action setDefaultRegions = (value: Record<string, string>[]) => (this.defaultRegions = value);

  @observable defaultMediumGeoAreas: Record<string, string>[] = [];
  @action setDefaultMediumGeoAreas = (value: Record<string, string>[]) => (this.defaultMediumGeoAreas = value);

  @observable defaultCities: Record<string, string>[] = [];
  @action setDefaultCities = (value: Record<string, string>[]) => (this.defaultCities = value);

  @observable defaultZipCodes: Record<string, string>[] = [];
  @action setDefaultZipCodes = (value: Record<string, string>[]) => (this.defaultZipCodes = value);

  @observable defaultBehaviors: Record<string, string>[] = [];
  @action setDefaultBehaviors = (value: Record<string, string>[]) => (this.defaultBehaviors = value);

  @observable defaultCustomAudiences: Record<string, string>[] = [];
  @action setDefaultCustomAudiences = (value: Record<string, string>[]) => (this.defaultCustomAudiences = value);

  @observable excludedCustomAudiences: Record<string, string>[] = [];
  @action setExcludedCustomAudiences = (value: Record<string, string>[]) => (this.excludedCustomAudiences = value);

  @observable defaultRelationshipStatuses: number[] = [];
  @action setDefaultRelationshipStatuses = (value: number[]) => (this.defaultRelationshipStatuses = value);
  /* ----------------------------------------------------------- */
  /* Article Expiration Settings Attributes
  /* ----------------------------------------------------------- */
  @observable articleExpirationSettings: ArticleExpirationSettings = {
    id: null,
    enabled: false,
    defaultDuration: 1,
    ruleType: ArticleExpirationSettingsRuleTypeEnum.from_keywee_creation_date,
    expirationRules: []
  };
  @action setArticleExpirationSettings = (value: ArticleExpirationSettings) => (this.articleExpirationSettings = value);
  /* ----------------------------------------------------------- */
  /* Organic Upload Settings Attributes
  /* ----------------------------------------------------------- */
  @observable organicUploadSettings: OrganicUploadSettings = {
    id: null,
    enabled: true,
    type: OrganicUploadSettingsTypeEnum.performance,
    maxArticlesToUpload: 1,
    creativeCallToAction: OrganicUploadSettingsCreativeCallToActionEnum.SIGN_UP,
    enableCreativeApproval: false,
    enableCreativeSuggestions: false,
    maxCreativeSuggestions: 3,
    enableLeadgenFormCreation: false,
    enableLeadgenFormNamePrefix: false,
    excludeTaggedSponsoredPosts: true,
    leadgenFormExternalId: '',
    leadgenFormNamePrefix: '',
    rssSettings: null
  };
  @action setOrganicUploadSettings = (value: OrganicUploadSettings) => (this.organicUploadSettings = value);
  /* ----------------------------------------------------------- */
  /* Update params in batch
  /* ----------------------------------------------------------- */
  @action setParams(fields: Partial<this>) {
    Object.entries(fields).forEach(([key, value]) => {
      const fieldKey = key as keyof this;

      if (fieldKey in this) {
        (this[fieldKey] as this[typeof fieldKey]) = value as this[typeof fieldKey];
      }
    });
  }

  @action reset = () => {
    this.id = null;
    this.isDrawerOpen = false;
    this.isLiveCampaign = false;

    this.name = '';
    this.end = moment().add(1, 'days').format('YYYY-MM-DD');
    this.useCase = null;
    this.defaultDailyBudget = 0;
    this.defaultObjective = 'LINK_CLICKS';
    this.optimizationGoal = 'LINK_CLICKS';

    this.whitelistDomains = '';
    this.utmParameters = '';
    this.adMacroTags = '';
    this.whitelistedUrlParams = '';
    this.urlSegment = '';

    this.campaignFacebookPages = [];
    this.unifiedTrackingPixels = [];

    this.automaticAdPlacementEnabled = false;
    this.defaultPlacementPublisherPlatforms = null;
    this.defaultPlacementDevicePlatforms = null;
    this.defaultPlacementFacebookPositions = null;
    this.defaultPlacementInstagramPositions = null;
    this.defaultPlacementMessengerPositions = null;

    this.defaultUserDevice = [];
    this.defaultUserOs = null;

    this.defaultAgeMin = 18;
    this.defaultAgeMax = 65;
    this.defaultGender = 0;
    this.language = 'en';
    this.defaultLocalesFromLanguage = false;
    this.defaultCountries = [];
    this.defaultRegions = [];
    this.defaultMediumGeoAreas = [];
    this.defaultCities = [];
    this.defaultZipCodes = [];
    this.defaultBehaviors = [];
    this.defaultCustomAudiences = [];
    this.excludedCustomAudiences = [];
    this.defaultRelationshipStatuses = [];

    this.articleExpirationSettings = {
      id: null,
      enabled: false,
      defaultDuration: 1,
      ruleType: ArticleExpirationSettingsRuleTypeEnum.from_keywee_creation_date,
      expirationRules: []
    };

    this.organicUploadSettings = {
      id: null,
      enabled: true,
      type: OrganicUploadSettingsTypeEnum.performance,
      maxArticlesToUpload: 1,
      creativeCallToAction: OrganicUploadSettingsCreativeCallToActionEnum.SIGN_UP,
      enableCreativeApproval: false,
      enableCreativeSuggestions: false,
      maxCreativeSuggestions: 3,
      enableLeadgenFormCreation: false,
      enableLeadgenFormNamePrefix: false,
      excludeTaggedSponsoredPosts: true,
      leadgenFormExternalId: '',
      leadgenFormNamePrefix: '',
      rssSettings: null
    };
  };
  /* ----------------------------------------------------------- */
  /* Computed Values
  /* ----------------------------------------------------------- */
  @computed get publisherFacebookPages(): PublisherFacebookPage[] | null {
    if (!this.campaignFacebookPages.length) return null;

    return this.campaignFacebookPages.map(page => ({
      instagramAccountId: page.instagramAccountId,
      instagramAccountType: page.instagramAccountType,
      facebookPage: {
        externalId: page.facebookPageId,
        name: page.facebookPageDisplayName,
        newPageExperience: Boolean(page.newPageExperience)
      }
    }));
  }

  @computed get articleExpirationSettingsAttributes(): ArticleExpirationSettingsInput {
    return {
      id: this.articleExpirationSettings.id,
      enabled: this.articleExpirationSettings.enabled,
      defaultDuration: this.articleExpirationSettings.defaultDuration,
      ruleType: this.articleExpirationSettings.ruleType,
      expirationRulesAttributes: this.articleExpirationSettings.expirationRules
    };
  }

  @computed get campaignParams(): SelfServiceCampaignInput {
    return {
      name: this.name,
      end: this.end,
      useCase: this.useCase,
      defaultDailyBudget: this.defaultDailyBudget,
      defaultObjective: this.defaultObjective,
      optimizationGoal: this.optimizationGoal,
      defaultArbitrageOptimizationStrategy: this.defaultArbitrageOptimizationStrategy,
      whitelistDomains: this.whitelistDomains,
      utmParameters: this.utmParameters,
      adMacroTags: this.adMacroTags,
      whitelistedUrlParams: this.whitelistedUrlParams,
      urlSegment: this.urlSegment,
      campaignFacebookPages: this.campaignFacebookPages,
      unifiedTrackingPixels: this.unifiedTrackingPixels,
      automaticAdPlacementEnabled: this.automaticAdPlacementEnabled,
      defaultPlacementPublisherPlatforms: this.defaultPlacementPublisherPlatforms,
      defaultPlacementDevicePlatforms: this.defaultPlacementDevicePlatforms,
      defaultPlacementFacebookPositions: this.defaultPlacementFacebookPositions,
      defaultPlacementInstagramPositions: this.defaultPlacementInstagramPositions,
      defaultPlacementMessengerPositions: this.defaultPlacementMessengerPositions,
      defaultUserDevice: this.defaultUserDevice,
      defaultUserOs: this.defaultUserOs,
      defaultAgeMin: this.defaultAgeMin,
      defaultAgeMax: this.defaultAgeMax,
      defaultGender: this.defaultGender,
      language: this.language,
      defaultLocalesFromLanguage: this.defaultLocalesFromLanguage,
      defaultCountries: this.defaultCountries,
      defaultRegions: this.defaultRegions,
      defaultMediumGeoAreas: this.defaultMediumGeoAreas,
      defaultCities: this.defaultCities,
      defaultZipCodes: this.defaultZipCodes,
      defaultBehaviors: this.defaultBehaviors,
      defaultCustomAudiences: this.defaultCustomAudiences,
      excludedCustomAudiences: this.excludedCustomAudiences,
      defaultRelationshipStatuses: this.defaultRelationshipStatuses,
      organicUploadSettingsAttributes: this.organicUploadSettings,
      articleExpirationSettingsAttributes: this.articleExpirationSettingsAttributes
    };
  }
  /* ----------------------------------------------------------- */
  /* Side Effects
  /* ----------------------------------------------------------- */
  constructor() {
    observe(this, 'useCase', this.handleUseCaseChange);
    observe(this, 'isDialogOen', this.handleIsDialogOenChange);
    observe(this, 'defaultObjective', this.handleDefaultObjectiveChange);
  }
  /* ----------------------------------------------------------- */
  /* Callbacks
  /* ----------------------------------------------------------- */

  handleIsDialogOenChange = () => {
    if (this.isDialogOen) return;

    this.reset();
  };

  handleUseCaseChange = (change: IValueDidChange<this['useCase']>) => {
    this.optimizationGoal = 'LINK_CLICKS';
    this.defaultObjective = 'LINK_CLICKS';
    this.defaultArbitrageOptimizationStrategy = change.newValue === 'ADS_ARBITRAGE' ? 'traffic' : null;
  };

  handleDefaultObjectiveChange = (change: IValueDidChange<this['defaultObjective']>) => {
    if (change.newValue === 'LINK_CLICKS') {
      this.optimizationGoal = 'LINK_CLICKS';
      this.organicUploadSettings = { ...this.organicUploadSettings, enableLeadgenFormCreation: false };
    }

    if (change.newValue === 'LEAD_GENERATION') {
      this.optimizationGoal = 'LEAD_GENERATION';
    }
  };
}

export default CampaignSettingsStore;
